import { CreateQRCodeIcon } from '@app/assets';
import { Button, Container, Input, OptionItem, Select, Typography } from '@app/components';
import { clsxm } from '@app/styles/clsxm';
import { Trans, useTranslation } from 'react-i18next';
import { TeamTable } from './TeamTable';
import { Dialog } from '@app/hoc';
import { useIsMobile } from '@app/hooks/useIsMobile.hook';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useHandler } from '@app/hooks/useHandler.hook';
import { ETeamMemberRole } from '@app/swagger-types';
import { useInviteTeamMembers } from '../api/team.queries';
import { Controller, useForm } from 'react-hook-form';
import { enumValues } from '@app/utils/assertion.utils';
import { humanizeEnum } from '@app/utils/string.utils';
import { email } from '@app/utils/yup.utils';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { usePlanLimits } from '@app/domain/plan/hooks/usePlanLimits';
import { useCurrentTeam } from '../hooks/useCurrentTeam';

type Props = {
  className?: string;
};

export const AccountTeamManagement: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const { canUseTeam } = usePlanLimits();
  const { openInviteMemberDialog: inviteMemberHandler } = useInviteMemberDialog();
  const { isVisibleToAllOrOwnerOnly } = useCurrentTeam();

  return (
    <Container className={clsxm('flex flex-col gap-6 p-0 lg:rounded-xl', className)}>
      <div className={clsxm('flex flex-col gap-6 pt-6 px-6 lg:rounded-xl', className)}>
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
          <div className="flex flex-col gap-4 col-span-1 lg:col-span-8">
            <Typography className="text-lg text-gray-800 font-bold">{t('team.teamMembersSection.title')}</Typography>
            <Typography variant="s">{t('team.teamMembersSection.description')}</Typography>
          </div>
          <Button
            startIcon={<CreateQRCodeIcon strokeWidth={2} className="w-3 h-3 text-nowrap" />}
            onClick={inviteMemberHandler}
            disabled={!canUseTeam || !isVisibleToAllOrOwnerOnly}
            className="col-span-1 lg:col-span-2 flex self-start lg:mr-0 mr-auto"
          >
            {t('team.teamMembersSection.inviteMemberButton')}
          </Button>
        </div>
      </div>

      <TeamTable />

      <ConfirmationRemoveMemberDialog
        id="confirmation-password-dialog"
        onConfirm={() => {}}
        memberMail="unknown.user@mail.com"
      />
    </Container>
  );
};

export const ConfirmationRemoveMemberDialog = NiceModal.create(
  ({ onConfirm, memberMail }: { onConfirm?: () => void; memberMail: string }) => {
    const isMobile = useIsMobile();
    const { remove: removeDialog, visible } = useModal();
    const { t } = useTranslation();
    const { isVisibleToAllOrOwnerOnly } = useCurrentTeam();

    const handlerConfirm = async () => {
      if (!isVisibleToAllOrOwnerOnly) {
        toast.error(t('toaster.notAllowedToDeleteMember'));
        return;
      }
      onConfirm?.();
      removeDialog();
    };

    return (
      <Dialog
        open={visible}
        onClose={() => {
          removeDialog();
        }}
        hideHeader
        paperClassName="text-accept-dark-blue shadow-none bg-transparent rounded-none"
        maxWidth="md"
        fullScreen={isMobile}
      >
        <div className="flex flex-col gap-4 pt-4 md:pt-0">
          <div
            className={clsxm(
              'rounded-2xl bg-white shadow overflow-hidden',
              // extra space needed on mobile to avoid overlap with support chat FAB on prod
              'pb-20 md:pb-0'
            )}
          >
            <div className="flex flex-col gap-6 p-8 max-w-[504px]">
              <div className="flex flex-col gap-4">
                <h2 className="text-xl text-gray-800 font-bold">{t('team.removeMemberDialog.title')}</h2>
                <p className="text-s text-gray-700">
                  <Trans i18nKey={'team.removeMemberDialog.description'} values={{ email: memberMail }}>
                    <b />
                  </Trans>
                </p>
              </div>
              <div className="flex gap-4 w-full">
                <Button className="flex-1" variant="outlined" onClick={removeDialog}>
                  {t('dialog.cancel')}
                </Button>
                <Button className="flex-1" onClick={handlerConfirm}>
                  {t('team.removeMemberDialog.removeMemberButton')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    );
  }
);

export const InviteMemberDialog = NiceModal.create(() => {
  const { t } = useTranslation();
  const { isVisibleToAllOrOwnerOnly } = useCurrentTeam();

  const { mutate: inviteMember, isLoading } = useInviteTeamMembers();
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      email: '',
      teamMemberRole: ETeamMemberRole.MEMBER,
    },
    resolver: yupResolver(
      yup.object().shape({
        email: email(),
        teamMemberRole: yup.mixed().oneOf(enumValues(ETeamMemberRole)),
      })
    ),
  });

  const isEmptyEmail = watch('email') === '' || errors.email?.message !== undefined;

  const inviteMemberHandler = async ({ email, teamMemberRole }: { email: string; teamMemberRole: ETeamMemberRole }) => {
    await inviteMember(
      {
        email: email,
        role: teamMemberRole,
      },
      {
        onSuccess: () => {
          toast.success(t('toaster.memberInvited'));
          reset();
          removeInviteMemberDialog();
        },
      }
    );
  };

  const teamMemberRoles = enumValues(ETeamMemberRole);

  const { remove: removeInviteMemberDialog, visible } = useModal();
  const isMobile = useIsMobile();

  const onSubmit = handleSubmit(inviteMemberHandler);

  const isInviteDisabled = !isVisibleToAllOrOwnerOnly || isEmptyEmail || isLoading;

  return (
    <Dialog
      open={visible}
      onClose={() => {
        removeInviteMemberDialog();
      }}
      hideHeader
      paperClassName="text-accept-dark-blue shadow-none bg-transparent rounded-none min-w-[504px]"
      maxWidth="md"
      fullScreen={isMobile}
    >
      <div className="flex flex-col gap-4 pt-4 md:pt-0">
        <div
          className={clsxm(
            'rounded-2xl bg-white shadow overflow-hidden',
            // extra space needed on mobile to avoid overlap with support chat FAB on prod
            'pb-20 md:pb-0'
          )}
        >
          <form onSubmit={onSubmit} className={clsxm('p-8 rounded-3xl flex flex-col gap-6')}>
            <Typography variant="xl" className="text-xl font-bold">
              {t('team.inviteMemberDialog.title')}
            </Typography>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={t('team.inviteMemberDialog.emailLabel')}
                  className="disabled:opacity-100"
                  withBG
                  error={!!fieldState.error}
                  errorMessage={fieldState.error?.message}
                  autoComplete="email"
                  placeholder={t('team.inviteMemberDialog.emailPlaceholder')}
                />
              )}
            />
            <div className="pointer-events-none">
              <Controller
                name={'teamMemberRole'}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    onChange={onChange}
                    displayEmpty
                    disabled={true}
                    inputProps={{ label: t('team.inviteMemberDialog.roleLabel') }}
                  >
                    {teamMemberRoles.map((role) => (
                      <OptionItem
                        key={role}
                        value={role}
                        placeholder={t('team.inviteMemberDialog.rolePlaceholder')}
                        selected={value === role}
                        className="focus-within::!bg-gray-100 !bg-transparent hover:!bg-gray-100 focus:!bg-gray-100"
                      >
                        {humanizeEnum(role)}
                      </OptionItem>
                    ))}
                  </Select>
                )}
              />
            </div>
            <div className="flex gap-4 w-full">
              <Button className="flex-1" variant="outlined" type="button" onClick={() => removeInviteMemberDialog()}>
                {t('dialog.cancel')}
              </Button>
              <Button loading={isLoading} className="flex-1" type="submit" disabled={isInviteDisabled}>
                {t('team.inviteMemberDialog.inviteButton')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
});

export const useInviteMemberDialog = () => {
  const { remove: removeInviteMemberDialog } = useModal(InviteMemberDialog);

  const openInviteMemberDialog = useHandler(() => {
    NiceModal.show(InviteMemberDialog);
  });

  const memoizedRemoveInviteMemberDialog = useHandler(() => {
    removeInviteMemberDialog();
  });

  return { openInviteMemberDialog, removeInviteMemberDialog: memoizedRemoveInviteMemberDialog };
};
